import { memo, useEffect, useRef } from "react";
import Highlighter from "react-highlight-words";
import { findHighlightChunks } from "../helper";
import { BOLD_PREFIX_PATTERNS } from "./clausePatterns";

type ClauseHighlightProps = {
  highlightedText: string[];
  textToHighlight: string;
};

// Renders behind the document text: only the highlighted clause and clause-number prefixes are visible
export const ClauseHighlight = memo(({ highlightedText, textToHighlight }: ClauseHighlightProps) => {
  return (
    <span className="absolute top-0 right-0 bottom-0 left-0 -z-10 text-[#000000]">
      <Highlighter
        highlightClassName="bg-[#FFF873] text-[#000000]"
        searchWords={highlightedText}
        findChunks={findHighlightChunks}
        autoEscape={false}
        textToHighlight={textToHighlight}
        highlightTag={StaticTextHighlight}
        unhighlightTag={UnHighlightedTag}
      />
    </span>
  );
});

const UnHighlightedTag = memo(({ children }: any) => {
  return (
    <Highlighter
      searchWords={BOLD_PREFIX_PATTERNS}
      findChunks={findHighlightChunks}
      textToHighlight={children}
      highlightTag={"span"}
      highlightClassName="bold-highlight text-black"
      unhighlightClassName="invisible"
      unhighlightStyle={{
        contain: "layout style paint",
        contentVisibility: "hidden",
      }}
    />
  );
});

const StaticTextHighlight = memo(({ className, children, highlightIndex, ...props }: any) => {
  const spanRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (highlightIndex !== 0) return;
      spanRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }, 350);
    return () => clearTimeout(timeout);
  }, [children, highlightIndex]);

  return (
    <span key={children} ref={spanRef} className={className} {...props}>
      <Highlighter
        searchWords={BOLD_PREFIX_PATTERNS}
        findChunks={findHighlightChunks}
        textToHighlight={children}
        highlightClassName="bold-highlight text-black"
        highlightTag={"span"}
        unhighlightClassName="invisible"
      />
    </span>
  );
});
